import type { Balance } from './types';

/**
 * Default balance. All tables are literal integers on purpose: the sim must never
 * call Math.pow / Math.round on floats at runtime, or V8 and JavaScriptCore may
 * disagree on the last bit and desync client vs server.
 *
 * Units: positions in milli-cells (1 cell = 1000), time in ticks (20 ticks/s).
 * The board is one grid: enemies enter at row 0 and walk to the base below the last row,
 * pathing around obstacles and structures. Ranges are therefore short: 3 cells is a lot.
 */
export const defaultBalance: Balance = {
  tickMs: 50,
  // 2.1^k * 100, k = 0..7. Merging two equal units should be clearly better than keeping both (>2x).
  levelMult: [100, 210, 441, 926, 1945, 4084, 8577, 18012],
  maxLevel: 8,
  units: {
    rapid: { hp: 60, dmg: 4, cooldown: 8, range: 3200 },
    splash: { hp: 60, dmg: 12, cooldown: 30, range: 4000, splashRadius: 1100, groundOnly: true },
    burn: { hp: 60, dmg: 2, cooldown: 20, range: 2800, burnDmg: 3, burnTicks: 60 },
    chain: { hp: 60, dmg: 6, cooldown: 24, range: 3200, chainTargets: 3, chainRadius: 1300 },
    repair: { hp: 50, dmg: 0, cooldown: 0, range: 0, heal: 2, healInterval: 20 },
    barricade: { hp: 320, dmg: 0, cooldown: 0, range: 0 },
    // Melee line: cheap bodies that hold the road.
    barracks: {
      hp: 120,
      dmg: 0,
      cooldown: 0,
      range: 0,
      spawnInterval: 70,
      spawnMax: 2,
      soldier: { hp: 45, dmg: 5, interval: 10, speed: 60, range: 650 },
    },
    // Ranged: fragile, shoot from 2.6 cells, stand behind the melee line.
    archery: {
      hp: 90,
      dmg: 0,
      cooldown: 0,
      range: 0,
      spawnInterval: 90,
      spawnMax: 2,
      soldier: { hp: 25, dmg: 4, interval: 12, speed: 55, range: 2600 },
    },
    // Fast heavy: one rider at a time, arrives first, hits hard.
    stable: {
      hp: 110,
      dmg: 0,
      cooldown: 0,
      range: 0,
      spawnInterval: 120,
      spawnMax: 1,
      soldier: { hp: 80, dmg: 9, interval: 10, speed: 110, range: 700 },
    },
    // Economy: gold after every cleared wave. Merging mines is always worth it.
    mine: { hp: 80, dmg: 0, cooldown: 0, range: 0, income: 6 },
  },
  unitWeights: {
    rapid: 26,
    splash: 16,
    burn: 12,
    chain: 10,
    repair: 5,
    barricade: 10,
    barracks: 10,
    archery: 8,
    stable: 5,
    mine: 6,
  },
  enemies: {
    walker: { hp: 30, speed: 50, attackDmg: 1, attackInterval: 4, bounty: 2 },
    runner: { hp: 15, speed: 100, attackDmg: 1, attackInterval: 6, bounty: 2 },
    brute: { hp: 120, speed: 30, attackDmg: 3, attackInterval: 4, bounty: 6 },
    boss: { hp: 500, speed: 25, attackDmg: 2, attackInterval: 1, bounty: 40 },
    // Flies over the maze: barricades and fighters mean nothing to it, only shooters, riflemen and the hero.
    flyer: { hp: 22, speed: 70, attackDmg: 1, attackInterval: 5, bounty: 3, flying: true },
    // Shrugs off weak hits: an MG nest does 1 per shot to it. Big hits, merged buildings and fire get through.
    armored: { hp: 90, speed: 35, attackDmg: 2, attackInterval: 4, bounty: 5, armor: 3 },
  },
  enemyMeleeRange: 600,
  soldierCap: 6,
  // round(10 * 1.12^n), n = 0..30; the per-archetype base price below scales it (10 = as is).
  unitPrice: [
    10, 11, 13, 14, 16, 18, 20, 22, 25, 28, 31, 35, 39, 44, 49, 55, 61, 69, 77, 86, 96, 108, 121, 136, 152, 170, 190,
    213, 239, 267, 299,
  ],
  // Picking a shop slot is picking where the gold goes: sandbags are cheap, a tesla or a garage is a commitment.
  unitBasePrice: {
    rapid: 10,
    splash: 12,
    burn: 12,
    chain: 14,
    repair: 8,
    barricade: 6,
    barracks: 12,
    archery: 11,
    stable: 15,
    mine: 9,
  },
  pocketSlots: 3,
  // Level 3 takes four copies, level 5 sixteen: the first trait is a normal run's goal, the second a build to chase.
  traitLevels: [3, 5],
  traits: {
    rapid: ['twin', 'pierce'],
    splash: ['wide', 'flak'],
    burn: ['spread', 'inferno'],
    chain: ['fork', 'shock'],
    repair: ['crew', 'fast_heal'],
    barricade: ['spikes', 'regen'],
    barracks: ['squad', 'veterans'],
    archery: ['longbow', 'volley'],
    stable: ['pair', 'charge'],
    mine: ['vault', 'interest'],
  },
  // +25 HP, round(20 * 1.3^n)
  wallRepair: { hp: 25, prices: [20, 26, 34, 44, 57, 74, 97, 126, 163, 212, 276] },
  rerollBase: 5,
  rerollStep: 5,
  waveGoldBase: 12,
  waveGoldPerWave: 8,
  sellRefundPct: 50,
  startGold: 40,
  wallHp: 100,
  maxBattleTicks: 20 * 240,
  shopSize: 3,
  xpCosts: [30, 60, 100, 150, 210, 280, 360, 450, 550, 660, 780, 910],
  xpMergeBase: 10,
  xpWave: 15,
  // Heroes walk out of the bunker at the start of every fight. The commander hits hard and shockwaves;
  // the medic hits softer but keeps the defence standing. Hero level is shared, the medic unlocks at 3.
  heroes: {
    commander: { hp: 180, dmg: 10, interval: 10, speed: 80, range: 700, skill: { kind: 'shockwave', every: 5, radius: 1200 }, unlockLevel: 1 },
    medic: { hp: 150, dmg: 7, interval: 10, speed: 90, range: 700, skill: { kind: 'mend', every: 4, wallHeal: 6, structureHeal: 25 }, unlockLevel: 3 },
    // Ranged and fragile; every third shot is a triple-damage armour-piercing round. Anti-boss, anti-armour.
    sniper: { hp: 120, dmg: 14, interval: 16, speed: 70, range: 2800, skill: { kind: 'snipe', every: 3, pct: 300 }, unlockLevel: 6 },
  },
  // Profile progression. A card at max level doubles the building's base: about one merge level.
  cardMult: [100, 108, 117, 126, 136, 147, 159, 172, 186, 200],
  cardCopies: [2, 3, 4, 6, 8, 10, 13, 16, 20],
  heroMult: [100, 112, 125, 140, 157, 176, 197, 221, 247, 277],
  heroXpCosts: [50, 80, 120, 170, 230, 300, 380, 470, 570],
  rewards: { copiesPerWave: 1, winBonusCopies: 3, heroXpPerWave: 10, winBonusHeroXp: 40, medalsPerWave: 2, winBonusMedals: 10 },
  // The level ladder: six chapters of ten. Boards grow, the roster widens, the fifth and tenth levels bring a boss;
  // the tenth level of chapters 1–5 is the authored campaign map. Numbers here are the first cut, bots check them.
  ladder: {
    chapters: [
      // The whole board below the two enemy rows is buildable from wave 1, but most of it is under rocks (5 October):
      // a 5×5 zone with 55 % rocks leaves about ten free cells, and every clearing competes with a building for gold.
      { id: 'outskirts', levels: 10, width: 5, height: 7, zoneRocksPct: 55, zonePermanentPct: 0, topRocks: [0, 2], rockCost: [5, 15], roster: ['walker', 'runner'], hpStart: 70, hpGrowthPct: 121, hpPerLevelPct: 12, wavesMin: 5, wavesMax: 7, startGold: 40, wallHp: 100, finale: 'map01', medalsFirstWin: 10 },
      { id: 'junction', levels: 10, width: 6, height: 8, zoneRocksPct: 58, zonePermanentPct: 10, topRocks: [1, 3], rockCost: [10, 20], roster: ['walker', 'runner', 'brute'], hpStart: 115, hpGrowthPct: 118, hpPerLevelPct: 10, wavesMin: 6, wavesMax: 8, startGold: 45, wallHp: 110, finale: 'map02', medalsFirstWin: 15 },
      { id: 'industrial', levels: 10, width: 6, height: 8, zoneRocksPct: 60, zonePermanentPct: 20, topRocks: [1, 3], rockCost: [10, 25], roster: ['walker', 'runner', 'brute', 'flyer'], hpStart: 160, hpGrowthPct: 118, hpPerLevelPct: 5, wavesMin: 7, wavesMax: 9, startGold: 45, wallHp: 120, finale: 'map03', medalsFirstWin: 20 },
      { id: 'bridge', levels: 10, width: 6, height: 9, zoneRocksPct: 62, zonePermanentPct: 25, topRocks: [1, 3], rockCost: [15, 25], roster: ['walker', 'runner', 'brute', 'flyer', 'armored'], hpStart: 205, hpGrowthPct: 119, hpPerLevelPct: 6, wavesMin: 8, wavesMax: 10, startGold: 50, wallHp: 130, finale: 'map04', medalsFirstWin: 25 },
      { id: 'canyon', levels: 10, width: 7, height: 9, zoneRocksPct: 64, zonePermanentPct: 30, topRocks: [1, 4], rockCost: [15, 30], roster: ['walker', 'runner', 'brute', 'flyer', 'armored'], hpStart: 285, hpGrowthPct: 119, hpPerLevelPct: 7, wavesMin: 8, wavesMax: 10, startGold: 55, wallHp: 150, finale: 'map05', medalsFirstWin: 30, bossAbilities: ['summon', 'smash', 'regen'] },
      { id: 'frontier', levels: 10, width: 7, height: 9, zoneRocksPct: 66, zonePermanentPct: 35, topRocks: [1, 4], rockCost: [20, 30], roster: ['walker', 'runner', 'brute', 'flyer', 'armored'], hpStart: 320, hpGrowthPct: 119, hpPerLevelPct: 4, wavesMin: 9, wavesMax: 12, startGold: 60, wallHp: 160, medalsFirstWin: 40, bossAbilities: ['summon', 'smash', 'regen'], doubleBoss: true },
    ],
  },
  // Invite a friend: the friend must clear five waves once (a real player, not a bot farm), then both get 30 medals.
  referral: { goalWaves: 5, medalsEach: 30, maxInvites: 50 },
  // Three challenges a week from this pool; each pays its medals once per week. Ids double as theme keys.
  challenges: {
    perWeek: 3,
    pool: [
      { id: 'blitz_l3', mapId: 'l3', modifiers: ['fast_enemies'], medals: 20 },
      { id: 'glass_l7', mapId: 'l7', modifiers: ['glass_bunker'], medals: 25 },
      { id: 'iron_outskirts', mapId: 'map01', modifiers: ['iron_horde'], medals: 30 },
      { id: 'air_outskirts', mapId: 'map01', modifiers: ['air_raid', 'no_hero'], medals: 35 },
      { id: 'glass_outskirts', mapId: 'map01', modifiers: ['glass_bunker', 'fast_enemies'], medals: 40 },
      { id: 'poor_junction', mapId: 'map02', modifiers: ['poor', 'only_shooters'], medals: 45 },
      { id: 'soldiers_junction', mapId: 'map02', modifiers: ['only_soldiers', 'tough_enemies'], medals: 50 },
      { id: 'blitz_junction', mapId: 'map02', modifiers: ['fast_enemies', 'air_raid'], medals: 45 },
      { id: 'lone_industrial', mapId: 'map03', modifiers: ['no_hero', 'iron_horde'], medals: 60, goalWaves: 10 },
      { id: 'survive_endless', mapId: 'endless01', modifiers: ['tough_enemies'], medals: 60, goalWaves: 12 },
    ],
  },
  // Consecutive days played: the first run of the day pays medals, more with each day, a week caps it.
  streak: { medals: [4, 6, 8, 10, 12, 15, 20] },
  // Skill tree, four branches. A full tree is 1 660 medals: at 5 rewarded runs a day that is about two weeks.
  skills: {
    costs: [10, 20, 35, 55, 80],
    nodes: [
      { id: 'turret_dmg', branch: 'fire', maxLevel: 5 },
      { id: 'turret_range', branch: 'fire', maxLevel: 3, requires: 'turret_dmg' },
      { id: 'structure_hp', branch: 'defense', maxLevel: 5 },
      { id: 'wall_hp', branch: 'defense', maxLevel: 5, requires: 'structure_hp' },
      { id: 'soldier_power', branch: 'defense', maxLevel: 5, requires: 'wall_hp' },
      { id: 'start_gold', branch: 'economy', maxLevel: 5 },
      { id: 'wave_gold', branch: 'economy', maxLevel: 5, requires: 'start_gold' },
      { id: 'mine_income', branch: 'economy', maxLevel: 3, requires: 'wave_gold' },
      { id: 'cheap_shop', branch: 'economy', maxLevel: 3, requires: 'mine_income' },
      { id: 'hero_power', branch: 'hero', maxLevel: 5 },
      { id: 'hero_speed', branch: 'hero', maxLevel: 3, requires: 'hero_power' },
    ],
  },
  // Hero gear: an epic set is roughly one hero level on every stat.
  gear: {
    bonus: {
      weapon: { common: [6, 10], rare: [12, 18], epic: [22, 30] },
      armor: { common: [8, 12], rare: [15, 22], epic: [28, 40] },
      boots: { common: [6, 10], rare: [12, 18], epic: [22, 30] },
      charm: { common: [8, 12], rare: [15, 22], epic: [28, 40] },
    },
    rarityWeights: { common: 70, rare: 25, epic: 5 },
    winRarityWeights: { common: 40, rare: 45, epic: 15 },
    dropMinWaves: 4,
    inventoryCap: 40,
    scrapXp: { common: 10, rare: 25, epic: 60 },
    mergeCount: 3,
    sets: { pairMinRarity: 'rare', pairPct: 5, fullEpicAoeEveryDelta: -1 },
  },
  shop: {
    starsUsd: 0.02,
    chests: [
      { id: 'crate', stars: 25, copies: 3, uniform: false, gearOdds: { common: 75, rare: 22, epic: 3 }, heroXp: 0, medals: 5 },
      { id: 'container', stars: 75, copies: 8, uniform: true, gearOdds: { common: 40, rare: 45, epic: 15 }, heroXp: 30, medals: 15 },
      { id: 'vault', stars: 200, copies: 20, uniform: true, gearOdds: { common: 0, rare: 0, epic: 100 }, heroXp: 100, medals: 40 },
    ],
    pityEvery: 10,
    freeChest: 'crate',
    copiesPack: { stars: 15, copies: 3 },
    heroXpPack: { stars: 30, xp: 200 },
    // Bought once: one copy of every card (10), 150 hero XP, 40 medals and a rare vest. Worth ~80 Stars at list prices.
    starterPack: { stars: 40, fullStars: 80, copiesEach: 1, heroXp: 150, medals: 40, gear: { slot: 'armor', rarity: 'rare', bonus: 18 } },
    // Field skins: pure cosmetics, one purchase each; the default is free.
    skins: [
      { id: 'default', stars: 0 },
      { id: 'night', stars: 50 },
      { id: 'winter', stars: 50 },
      { id: 'desert', stars: 50 },
    ],
  },
  // Three a day, drawn from this pool by the day seed. Rewards are modest next to a run: a reason to log in, not to grind.
  quests: {
    perDay: 3,
    pool: [
      { kind: 'waves', target: 5, copies: 2, heroXp: 20 },
      { kind: 'waves', target: 8, copies: 4, heroXp: 40 },
      { kind: 'merges', target: 6, copies: 2, heroXp: 20 },
      { kind: 'merges', target: 12, copies: 4, heroXp: 40 },
      { kind: 'kills', target: 60, copies: 2, heroXp: 20 },
      { kind: 'kills', target: 150, copies: 4, heroXp: 40 },
      { kind: 'runs', target: 2, copies: 2, heroXp: 20 },
      { kind: 'runs', target: 4, copies: 3, heroXp: 30 },
      { kind: 'free_chest', target: 1, copies: 1, heroXp: 15 },
      { kind: 'win_map', target: 1, copies: 5, heroXp: 60 },
    ],
  },
};
